import { AsyncPipe, CurrencyPipe, DecimalPipe, NgClass, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { DiscoverCTA, DiscoverFacade } from '@zero/discover/domain';
import { InstrumentDetailsLinkComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent, ModalComponent } from '@zero/shared/ui-web-design-system';
import { commonRoutes } from 'constants/web-app-constants';
import { NgArrayPipesModule } from 'ngx-pipes';
import { filter, map, take } from 'rxjs';
import { DiscoverCardType } from '../../utils/discover-card-type.enum';
import { CurrentSegment } from '../../utils/pipes/current-segment.pipe';
import { MarkdownPipe } from '../../utils/pipes/markdown.pipe';
import { RecordToArray } from '../../utils/pipes/record-to-array.pipe';
@Component({
  selector: 'discover-article',
  templateUrl: './discover-article.component.html',
  styleUrls: ['./discover-article.component.scss'],
  imports: [
    NgArrayPipesModule,
    TranslocoPipe,
    MarkdownPipe,
    RecordToArray,
    CurrentSegment,
    InstrumentDetailsLinkComponent,
    AsyncPipe,
    ButtonComponent,
    CurrencyPipe,
    DecimalPipe,
    NgClass,
    UpperCasePipe,
    ModalComponent,
    NgTemplateOutlet,
  ],
})
export class DiscoverArticleComponent implements OnInit, OnDestroy {
  @Input() public cardType?: DiscoverCardType;
  @Output() public handleClose = new EventEmitter<void>();

  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);

  public readonly facade = inject(DiscoverFacade);
  public readonly commonRoutes = commonRoutes;
  public readonly DiscoverCardType = DiscoverCardType;

  public initialSegment$ = this.facade.discoverListDetails$.pipe(map((details) => details?.segments[0]?.name));
  public hasCta$ = this.facade.discoverDetails$.pipe(
    filter((details) => !!details),
    map((details) => details?.cta?.text && (details?.cta?.link || details?.cta?.externalLink)),
  );

  public activeSegment: string;
  public isins: string[] = [];

  ngOnDestroy(): void {
    this.facade.unsubscribeFromDetailsInstruments();
    this.facade.unsubscribeFromListInstruments();
    this.facade.clearDiscoverDetails();
  }

  ngOnInit(): void {
    this.facade.subscribeToDetailsInstruments();
    this.facade.subscribeToListInstruments();
    this.#initItemsForMultiorder();
  }

  readonly #initItemsForMultiorder = () =>
    this.facade.discoverDetails$
      .pipe(
        filter((details) => !!details?.instruments && !!Object.keys(details?.instruments).length),
        take(1),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(({ instruments }) => (this.isins = Object.keys(instruments).map((isin) => isin)));

  public handleCtaButtonClick(cta: DiscoverCTA, articleId: string) {
    if (cta.isin) {
      this.#router.navigate([commonRoutes.postlogin.buyAndSell], {
        queryParams: {
          isin: cta.isin,
          direction: 'buy',
          fzcontent: 'cta',
          ...this.#getTrackingParams(cta.isin, articleId),
        },
      });
    } else if (cta.link) {
      this.#router.navigateByUrl(cta.link);
    } else if (cta.externalLink) {
      window.open(cta.externalLink, '_system');
    }
    this.handleClose.emit();
  }

  public handleMultiorderButtonClick(articleId: string) {
    this.#router.navigate([commonRoutes.postlogin.multiOrder], {
      queryParams: {
        isin: this.isins.join(','),
        fzcontent: 'button',
        ...this.#getTrackingParams(this.isins.join(','), articleId),
      },
    });
    this.handleClose.emit();
  }

  public handleBuyButtonClick(isin: string, articleId: string, index: number) {
    this.#router.navigate([commonRoutes.postlogin.buyAndSell], {
      queryParams: {
        isin,
        direction: 'buy',
        fzcontent: `instrumentlist-position-${++index}`,
        ...this.#getTrackingParams(articleId, isin),
      },
    });
    this.handleClose.emit();
  }

  public toggle = (event: Event, isin: string) =>
    (this.isins = (event.currentTarget as HTMLInputElement).checked ? [...this.isins, isin] : this.isins.filter((item) => item !== isin));

  #getTrackingParams(articleId: string, fzterm: string, index?: number) {
    return {
      fzsrc: 'zerocockpit',
      fzmedium: 'web',
      fzcampaign: `discover-${this.cardType}-${articleId}`,
      fzterm,
    };
  }
}
